import { zodResolver } from "@hookform/resolvers/zod";
import { Zap } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAllSchemas, useTriggerIngestion } from "@noa/api-client";
import { CustomJobFormFields } from "./custom-job-form";
import { CustomJobPreview } from "./custom-job-preview";
import {
  customJobDefaultValues,
  customJobSchema,
  PARSER_OPTIONS,
  SOURCE_TYPES,
  type CustomJobForm,
} from "~/utils/custom-job-form";
import { Alert, Button, message, Text, Title } from "~/components/ui";

type CustomJobPanelProps = {
  onNavigateToHistory?: () => void;
};

export function CustomJobPanel({ onNavigateToHistory }: CustomJobPanelProps) {
  const { t } = useTranslation("admin");
  const { schemas, isLoading: schemasLoading } = useAllSchemas();
  const [lastTaskId, setLastTaskId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { control, handleSubmit, watch } = useForm<CustomJobForm>({
    resolver: zodResolver(customJobSchema),
    defaultValues: customJobDefaultValues,
  });

  const values = watch();

  const schemaOptions = schemas.map((s) => ({
    value: s.schema_id,
    label: `${s.schema_id} — ${s.description || s.doc_type}`,
  }));

  const triggerJob = useTriggerIngestion();

  const handleTrigger = (form: CustomJobForm) => {
    triggerJob.mutate(
      {
        files: form.filesPattern.split(",").map((f) => f.trim()),
        source_type: form.sourceType,
        schema_id: form.schemaId || undefined,
        with_vision: form.withVision,
        skip_existing: form.skipExisting,
        parser: form.parser || undefined,
        max_records: form.limitEnabled ? form.maxRecords : undefined,
      },
      {
        onSuccess: (data) => {
          setLastTaskId(data.task_id);
          setError(null);
          void message.success(t("uploadAdmin.customJob.started"));
        },
        onError: () => {
          setError(t("uploadAdmin.customJob.error"));
        },
      }
    );
  };

  const sourceLabel = SOURCE_TYPES.find((s) => s.value === values.sourceType)?.label ?? values.sourceType;
  const selectedSchema = schemas.find((s) => s.schema_id === values.schemaId);
  const parserLabel = PARSER_OPTIONS.find((p) => p.value === values.parser)?.label;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Zap size={24} className="text-purple-500" />
        <div>
          <Title level={5} className="!mb-0">
            {t("uploadAdmin.customJob.title")}
          </Title>
          <Text tone="muted" className="text-sm">
            {t("uploadAdmin.customJob.description")}
          </Text>
        </div>
      </div>

      {/* Two-column layout: Form (1/3) + Preview (2/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CustomJobFormFields
          control={control}
          schemaOptions={schemaOptions}
          schemasLoading={schemasLoading}
          isSubmitting={triggerJob.isPending}
          onSubmit={handleSubmit(handleTrigger)}
        />

        <CustomJobPreview
          values={values}
          sourceLabel={sourceLabel}
          parserLabel={parserLabel}
          selectedSchema={selectedSchema}
        />
      </div>

      {/* Error message */}
      {error && (
        <Alert
          type="error"
          message={t("common:error")}
          description={error}
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      {/* Success message — dark mode compatible */}
      {lastTaskId && (
        <Alert
          type="success"
          message={t("uploadAdmin.sync.taskStarted")}
          description={
            <div className="flex flex-col gap-2">
              <Text>
                {t("uploadAdmin.sync.taskId")}:{" "}
                <code className="bg-noa-hover px-2 py-0.5 rounded text-sm">{lastTaskId}</code>
              </Text>
              {onNavigateToHistory ? (
                <Button variant="link" size="small" className="!p-0 !h-auto" onClick={onNavigateToHistory}>
                  {t("uploadAdmin.customJob.checkHistory")} →
                </Button>
              ) : (
                <Text tone="muted" className="text-sm">
                  {t("uploadAdmin.customJob.checkHistory")}
                </Text>
              )}
            </div>
          }
          showIcon
          closable
          onClose={() => setLastTaskId(null)}
        />
      )}
    </div>
  );
}
